import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { jmd } from "@/lib/format";
import { Zap } from "lucide-react";

function remaining() {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  let s = Math.max(0, Math.floor((end - now) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  s %= 3600;
  const m = String(Math.floor(s / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return { h, m, sec };
}

export default function DealOfDay() {
  const [p, setP] = useState(null);
  const [t, setT] = useState(remaining());
  useEffect(() => {
    const load = () => api.get("/deal-of-day").then(({ data }) => setP(data.deal)).catch(() => {});
    load();
    const poll = setInterval(load, 20000); // live sell-out: refresh remaining stock
    const i = setInterval(() => setT(remaining()), 1000);
    return () => { clearInterval(poll); clearInterval(i); };
  }, []);
  if (!p) return null;
  const allocation = p.allocation || 0;
  const remainingUnits = p.remaining != null ? p.remaining : allocation;
  const pct = allocation > 0 ? Math.max(4, Math.round((remainingUnits / allocation) * 100)) : 100;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="deal-of-day">
      <div className="relative overflow-hidden rounded-3xl bg-ink text-white sn-noise">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div className="p-8 sm:p-10">
            <span className="inline-flex items-center gap-2 text-coral font-bold text-xs uppercase tracking-widest">
              <Zap size={14} /> Deal of the Day
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3 leading-tight line-clamp-2">{p.name}</h2>
            <div className="flex items-end gap-3 mt-4">
              <span className="font-display text-3xl font-extrabold">{jmd(p.price)}</span>
              {p.prev_price > p.price && <span className="text-white/50 line-through">{jmd(p.prev_price)}</span>}
              {p.discount > 0 && <span className="bg-coral text-white text-sm font-bold px-2.5 py-1 rounded-full">-{p.discount}%</span>}
            </div>

            {allocation > 0 && (
              <div className="mt-5" data-testid="deal-stock-bar">
                <div className="flex items-center justify-between text-xs font-semibold mb-1.5">
                  <span className="text-coral uppercase tracking-wide">Today only</span>
                  <span className="text-white/70" data-testid="deal-stock-remaining">
                    {remainingUnits > 0 ? `${remainingUnits} of ${allocation} left` : "Sold out for today"}
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full rounded-full bg-coral transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 mt-5" data-testid="deal-countdown">
              {[["Hrs", t.h], ["Min", t.m], ["Sec", t.sec]].map(([l, v]) => (
                <div key={l} className="text-center bg-white/10 rounded-xl px-3 py-2 min-w-[56px]">
                  <div className="font-display text-xl font-bold tabular-nums">{v}</div>
                  <div className="text-[10px] text-white/60 uppercase tracking-wide">{l}</div>
                </div>
              ))}
            </div>
            <Link to={`/product/${p.id}`} data-testid="deal-cta"
              className="inline-flex items-center gap-2 mt-6 bg-coral text-white rounded-full px-7 py-3 font-semibold hover:bg-white hover:text-ink transition-colors">
              Grab the deal →
            </Link>
          </div>
          <Link to={`/product/${p.id}`} className="block h-full min-h-[240px]">
            <img src={assetUrl(p.image)} alt={p.name} className="h-full w-full object-cover" />
          </Link>
        </div>
      </div>
    </section>
  );
}
